import { useState, useEffect, useRef } from 'react';
import { request } from '../services/api';

export const useMonitorSocket = (deviceId) => {
    const [device, setDevice] = useState(null);
    const [sensorData, setSensorData] = useState({});
    const [wsConnected, setWsConnected] = useState(false);
    const [suspendedSensors, setSuspendedSensors] = useState(new Set());
    
    const failedAttempts = useRef({});
    const wsRef = useRef(null);
    const MAX_FAILURES = 5;

    // --- تابع تشخیص خودکار آدرس وب‌سوکت ---
    const getSocketUrl = () => {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    
    if (isLocalhost) {
        // به جای پورت مستقیم 8765، از آدرس سایت و مسیر امن استفاده کنید
        // این کار باعث می‌شود درخواست شما از Nginx عبور کند و تایید شود
        return 'wss://sfarm.tabangohar.com/ws'; 
    } else {
        return `wss://${hostname}/ws`;
    }
};

    // 1. دریافت اطلاعات پایه دستگاه از دیتابیس
    useEffect(() => {
        const getDeviceInfo = async () => {
            try {
                const res = await request('/get_devices.php');
                if (res.status === 'success') {
                    const target = res.devices.find(d => d.id == deviceId);
                    if (target) setDevice(target);
                }
            } catch (err) {
                console.error("API Error:", err);
            }
        };
        getDeviceInfo();
    }, [deviceId]);

    // 2. مدیریت اتصال WebSocket و چرخه اسکن
    useEffect(() => {
        if (!device) return;

        // ایجاد اتصال با آدرس هوشمند
        const wsUrl = getSocketUrl();
        console.log(`Connecting to: ${wsUrl}`);
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
            console.log("Monitor Socket Connected");
            setWsConnected(true);
            ws.send(JSON.stringify({ action: 'monitor', serial: device.device_serial }));
            requestScan(true);
        };

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if (data.raw) {
                parseDetailedReport(data.raw, data.type, data.index);
            }
        };

        ws.onclose = () => {
            setWsConnected(false);
            console.log("Socket Disconnected");
        };

        const scanInterval = setInterval(() => requestScan(false), 5000);

        return () => {
            clearInterval(scanInterval);
            ws.close();
        };
    }, [device]);

    // --- تابع درخواست اسکن هوشمند ---
    const requestScan = (forceAll = false) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && device) {
            const targets = [];
            const shouldScan = (key) => 
                forceAll || !failedAttempts.current[key] || failedAttempts.current[key] < MAX_FAILURES;

            ['temp', 'humi'].forEach(type => {
                for (let i = 0; i < 9; i++) {
                    if (shouldScan(`${type}${i}`)) targets.push(`${type}${i}`);
                }
            });

            if (shouldScan('wind0')) targets.push('wind');
            if (shouldScan('secu0')) targets.push('secu');
            if (shouldScan('gsm0')) targets.push('gsm');
            if (shouldScan('remote0')) targets.push('remote');

            for (let i = 0; i < 68; i++) {
                if (shouldScan(`dIn${i}`)) targets.push(`dIn${i}`);
            }

            for (let i = 0; i < 72; i++) {
                if (shouldScan(`dOut${i}`)) targets.push(`dOut${i}`);
            }

            if (targets.length > 0) {
                wsRef.current.send(JSON.stringify({
                    action: 'scan',
                    serial: device.device_serial,
                    targets: targets
                }));
            }
        }
    };

    // --- پارسر اصلی گزارش‌های متنی ---
    const parseDetailedReport = (rawText, type, index) => {
        const key = `${type}${index}`;
        const isDisconnected = rawText.includes("Disconnected");

        if (isDisconnected) {
            failedAttempts.current[key] = (failedAttempts.current[key] || 0) + 1;
            if (failedAttempts.current[key] >= MAX_FAILURES) {
                setSuspendedSensors(prev => {
                    const newSet = new Set(prev);
                    newSet.add(key);
                    return newSet;
                });
            }
        } else {
            failedAttempts.current[key] = 0;
            setSuspendedSensors(prev => {
                if (prev.has(key)) {
                    const newSet = new Set(prev);
                    newSet.delete(key);
                    return newSet;
                }
                return prev;
            });
        }

        const statusMatch = rawText.match(/status:\s*(\d+)(?:\s*\(By:\s*([^)]+)\))?/);
        const statusValue = statusMatch ? statusMatch[1] : null;
        const causedByValue = (statusMatch && statusMatch[2]) ? statusMatch[2] : "";

        let errorValue = "";
        if (rawText.includes("_____Error_____")) {
            const errorMatch = rawText.match(/\(Is depended on ([^)]+)\)/);
            errorValue = errorMatch ? errorMatch[1] : "Dependent";
        }

        let currentValue = "--";
        const valMatch = rawText.match(/(temperature|humidity|wind):\s*([\d\.]+)/i);
        if (valMatch) currentValue = valMatch[2];

        const trigMatch = rawText.match(/(trig|callTrig|cDTrig):\s*(\d)/i);
        const isTriggered = trigMatch ? (trigMatch[2] === "1") : false;

        const extract = (prop) => {
            const singleTypes = ['wind', 'secu', 'gsm', 'remote'];
            const safeIndex = (index !== undefined && index !== null) ? index : 0;
            const prefix = singleTypes.includes(type) ? type : `${type}${safeIndex}`;
            
            const regex = new RegExp(`${prefix}\\.${prop}=([\\d\\w\\-:,/\\.]+)`, 'i');
            const match = rawText.match(regex);
            return match ? match[1] : '';
        };

        setSensorData(prev => ({
            ...prev,
            [key]: {
                connected: !isDisconnected,
                current: currentValue,
                status: statusValue,
                causedBy: causedByValue,
                error: errorValue,
                triggered: isTriggered,
                settings: {
                    enable: extract('enable'),
                    alertSms: extract('alertSms'),
                    toDOut: extract('toDOut'),
                    reverse: extract('reverse'),
                    setpoint: extract('setpoint'),
                    hyster: extract('hyster'),
                    leadDelay: extract('leadDelay'),
                    lagDelay: extract('lagDelay'),
                    logicMode: extract('logicMode'),
                    timerMode: extract('timerMode'),
                    delayOn: extract('delayOn'),
                    delayOff: extract('delayOff'),
                    periodicOn: extract('periodicOn'),
                    periodicOff: extract('periodicOff'),
                    dailyOn: extract('dailyOn'),
                    dailyOff: extract('dailyOff'),
                    zone: extract('zone'),
                    armDelay: extract('armDelay'),
                    reactDelay: extract('reactDelay'),
                    timeout: extract('timeout'),
                    learnedNumber: extract('learnedNumber'),
                    callEnableSecu: extract('callEnableSecu'),
                    callToDOut: extract('callToDOut'),
                    learnedRemote: extract('learnedRemote'),
                    cDToDOut: extract('cDToDOut'),
                }
            }
        }));
    };

    // --- تابع ارسال فرمان ---
    const sendCommand = (type, index, prop, value) => {
        if (!wsRef.current || !device) return;

        const singleTypes = ['wind', 'secu', 'gsm', 'remote'];
        const prefix = singleTypes.includes(type) ? type : `${type}${index}`;
        
        const cmd = `${prefix}.${prop}=${value}`;
        console.log("Sending MQTT Command:", cmd);

        wsRef.current.send(JSON.stringify({
            action: 'command',
            serial: device.device_serial,
            command: cmd
        }));

        const key = `${type}${index}`;
        setSensorData(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                settings: { 
                    ...prev[key]?.settings, 
                    [prop]: value 
                }
            }
        }));
    };

    const handleRetryAll = () => {
        failedAttempts.current = {};
        setSuspendedSensors(new Set());
        requestScan(true);
    };

    return {
        device,
        sensorData,
        wsConnected,
        suspendedSensors,
        sendCommand,
        handleRetryAll
    };
};